import { h, defineComponent, SetupContext } from 'vue';
// @ts-ignore
import { BInput } from 'buefy/dist/esm/input';
import { ValidationError } from '@/entities/validation';
import { SetterCallback } from '@/ui/types';
import { Field, FieldProps } from './Field';
import { isKeyEnter, isKeyNumber } from '@/ui/helpers';

export interface InputFieldProps {
  type?: string;
  name: string;
  label: string;
  value: string | number | null;
  icon?: string;
  required: boolean;
  placeholder: string;
  disabled: boolean;
  readonly?: boolean;
  errors?: ValidationError[];
  hideLabel?: boolean;
  horizontal?: boolean;
  autocomplete?: string;
  maxlength?: string | number;
  classes?: string;
  tooltipText?: string;
  tooltipAppendToBody?: boolean;
}

export const InputField = defineComponent<InputFieldProps>({
  name: 'InputField',
  props: [
    'type', 'name', 'label', 'value', 'icon',
    'required', 'placeholder', 'errors', 'disabled', 'readonly',
    'hideLabel', 'horizontal', 'autocomplete', 'maxlength',
    'classes', 'tooltipText', 'tooltipAppendToBody',
  ],
  setup(props, context: SetupContext) {
    return function render() {
      const fieldProps: FieldProps = {
        label: props.label,
        name: props.name,
        required: props.required,
        errors: props.errors ?? [],
        hideLabel: props.hideLabel,
        horizontal: props.horizontal,
        disabled: props.disabled,
        readonly: props.readonly,
        tooltipText: props.tooltipText,
        tooltipAppendToBody: props.tooltipAppendToBody,
      };

      return h(Field, { props: fieldProps }, [
        h(BInput, {
          props: { value: props.value },
          class: props.classes ?? '',
          attrs: {
            ...context.attrs,
            maxlength: props.maxlength,
            name: props.name,
            placeholder: props.placeholder,
            icon: props.icon,
            type: props.type,
            disabled: props.disabled,
            readonly: props.readonly,
            autocomplete: props.autocomplete,
            'password-reveal': props.type === 'password' && props.value && props.value.toString().length > 0,
          },
          on: {
            input: (newValue: string) => {
              context.emit('input', newValue);
            },
            ...context.listeners,
          },
          nativeOn: {
            click: (e: MouseEvent) => {
              if (props.readonly) {
                // @ts-ignore
                e.target.select();
              }
            },
            keyup: (e: KeyboardEvent) => {
              if (isKeyEnter(e)) {
                context.emit('input:enter');
              }
            },
            keypress: (event : KeyboardEvent) => {
              if (props.type === 'number' && !isKeyNumber(event)) {
                return event.preventDefault();
              }
              return true;
            },
          },
        }),
      ]);
    };
  },
});

export function inputField(props: InputFieldProps, input: SetterCallback<string>, on: Record<string, Function> = {}) {
  return h(InputField, {
    props,
    on: { input, ...on },
  });
}
